// The advent calendar: which day it is in Sydney, which seed and mechanics
// that day gets, and which decoration it unlocks. Pure; no DOM.

import { hashString } from './rng.js';
import { MECHANICS } from './level-gen.js';

export const CAMPAIGN_YEAR = 2026;
export const TZ = 'Australia/Sydney';
export const FIRST_DAY = 1;
export const LAST_DAY = 24;

const MECHANIC_UNLOCK_DAY = { bauble: 2, star: 3, tangled: 5, bell: 6, golden: 7, split: 8 };

let formatter = null;
function sydneyParts(date) {
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric' });
  }
  const parts = {};
  for (const p of formatter.formatToParts(date)) {
    if (p.type === 'year' || p.type === 'month' || p.type === 'day') parts[p.type] = Number(p.value);
  }
  return parts;
}

/** Sydney-local calendar date of an instant: { year, month (1–12), day }. */
export function sydneyDate(date = new Date()) {
  return sydneyParts(date);
}

/** 1..24 during 1–24 December (Sydney time); 24 for the rest of December; otherwise null. */
export function dayIndexFor(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const { month, day } = sydneyParts(d);
  if (month !== 12) return null;
  if (day >= LAST_DAY) return LAST_DAY;
  return day;
}

export function seedForDay(day) {
  return hashString(`SOC-${CAMPAIGN_YEAR}-${day}`);
}

function devUnlocked() {
  if (typeof globalThis !== 'undefined' && globalThis.__SOC_DEV_UNLOCK__ === true) return true;
  if (typeof process !== 'undefined' && process.env && process.env.SOC_DEV_UNLOCK === '1') return true;
  return false;
}

export function isDayUnlocked(day, date = new Date()) {
  if (!Number.isInteger(day) || day < FIRST_DAY || day > LAST_DAY) return false;
  if (devUnlocked()) return true;
  const today = dayIndexFor(date);
  return today != null && day <= today;
}

/** Mechanics in play on a day; cumulative in MECHANICS order. */
export function mechanicsForDay(day) {
  const d = Number(day) || 1;
  return MECHANICS.filter((m) => MECHANIC_UNLOCK_DAY[m] <= d);
}

export function tierForDay(day) {
  if (day >= 15) return 3;
  if (day >= 7) return 2;
  return 1;
}

export const DECORATIONS = [
  { id: 'wattle-star', name: 'Wattle-star topper', unlockDay: 1, blurb: 'Golden wattle on top of the tree, the way it starts every December.' },
  { id: 'gum-wreath', name: 'Gum-leaf wreath', unlockDay: 3, blurb: 'Silvery gum leaves and red bottlebrush for the front door.' },
  { id: 'beach-cricket', name: 'Beach cricket set', unlockDay: 5, blurb: 'A plastic bat, a tennis ball and an esky for stumps.' },
  { id: 'backyard-bbq', name: 'Backyard barbecue', unlockDay: 8, blurb: 'Snags on, prawns waiting, someone always in charge of the tongs.' },
  { id: 'festive-cockatoo', name: 'Festive cockatoo', unlockDay: 10, blurb: 'A sulphur-crested cockatoo wearing a paper crown from a bon-bon.' },
  { id: 'road-trip-caravan', name: 'Road-trip caravan', unlockDay: 13, blurb: 'Packed and charged for the long drive to the coast.' },
  { id: 'solar-star', name: 'Solar-powered star', unlockDay: 15, blurb: 'Charges all day on the roof, glows all night for free.' },
  { id: 'community-tree', name: 'Community tree', unlockDay: 18, blurb: 'The whole street decorated it, one ornament each.' },
  { id: 'balcony-lights', name: 'Balcony lights', unlockDay: 21, blurb: 'Fairy lights on the railing of a third-floor flat.' },
  { id: 'summer-sunset', name: 'Australian summer sunset', unlockDay: 24, blurb: 'Warm dusk over the rooftops as every house switches on.' },
];

export function decorationForDay(day) {
  return DECORATIONS.find((d) => d.unlockDay === day) || null;
}

/** Free play uses everything the player has already met in the calendar. */
export function freePlayMechanics(seenDays = []) {
  const days = (seenDays || []).map(Number).filter((d) => Number.isFinite(d) && d >= FIRST_DAY);
  const latest = days.length ? Math.min(LAST_DAY, Math.max(...days)) : 1;
  return mechanicsForDay(latest);
}
